import React, { useEffect, useState } from 'react';

import { PanelsEnum } from '@ariksa/inventory-core/api';
import { useDisclosure } from '@chakra-ui/react';
import { orderBy } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { formatNumberWithComma, formatTooltip } from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { selectIdentityOverview } from 'containers/Dashboard/IdentityOverview/selectors';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { renderResourcesWithIconAndCount } from 'containers/Dashboard/utils/utils';

export const InsightsForNonHumanIdentities: React.FC = () => {
  const { insightsForNonHumanIdentities } = useSelector(selectIdentityOverview);
  const { data } = insightsForNonHumanIdentities;
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setTotal(
      (data?.['Unrotated credentials'] ?? 0) +
        (data?.['Cloud access keys'] ?? 0) +
        (data?.['Expired certificates'] ?? 0) +
        (data?.['Exposed credentials'] ?? 0),
    );
  }, [data]);

  const styles = {
    iconColor: customTheme.colors.insights,
    isLoading: insightsForNonHumanIdentities.isLoading,
  };

  const items = [
    {
      label: 'Credentials not rotated in 90 days',
      iconType: IconTypes.SyncPause,
      count: data?.['Unrotated credentials'] ?? 0,
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          iconType: IconTypes.SyncPause,
          panel: PanelsEnum.UnrotatedCredentials,
          total: data?.['Unrotated credentials'] ?? 0,
        });
        /*navigate(
          `/findings?tab=AllFindings&group_id=07fe60b5-ff78-4943-9a4d-5dac0e29468b`,
        );*/
      },
      tooltip: formatTooltip({
        header: 'Unrotated credentials',
        content:
          'Credentials that are not being periodically rotated (manually or automatically)',
        width: 48,
      }),
      ...styles,
    },
    {
      label: 'Expired certificates',
      iconType: IconTypes.Certificate,
      count: data?.['Expired certificates'] ?? 0,
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          iconType: IconTypes.Certificate,
          panel: PanelsEnum.ExpiredCertificates,
          total: data?.['Expired certificates'] ?? 0,
        });
        /*navigate(
          `/visibility/security-graph?query=${SearchQueryMapping.Show_expired_certificates}`,
        );*/
      },
      tooltip: formatTooltip({
        header: 'Expired certificates',
        content: 'SSL/TLS certificates that have expired',
        width: 32,
      }),
      ...styles,
    },
    {
      label: 'Cloud access keys',
      iconType: IconTypes.Key,
      count: data?.['Cloud access keys'] ?? 0,
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          iconType: IconTypes.Key,
          panel: PanelsEnum.CloudAccessKeys,
          total: data?.['Cloud access keys'] ?? 0,
        });
        /*navigate(
          `/inventory/cloud?resource_category=${Categories.Credential}&resource_type=${NativeResources.IamUserAccessKey}`,
        );*/
      },
      tooltip: formatTooltip({
        header: 'Cloud access keys',
        content:
          'IAM keys and other user keys used to access public clouds or SaaS',
        width: 48,
      }),
      ...styles,
    },
    {
      label: 'Exposed credentials',
      iconType: IconTypes.ExposedCredentials,
      count: data?.['Exposed credentials'] ?? 0,
      onClick: () => {
        allEntitiesDisclosure.onOpen();
        setDetails({
          iconType: IconTypes.ExposedCredentials,
          panel: PanelsEnum.ExposedCredentials,
          total: data?.['Exposed credentials'] ?? 0,
        });
        /*navigate(
          `/visibility/security-graph?query=${getSearchQuery(
            SearchQueryMapping.Show_resource_type_with_internet_accessible,
            [Categories.Credential],
          )}`,
        );*/
      },
      tooltip: formatTooltip({
        header: 'Exposed credentials',
        content: 'Unauthorized presence of credentials outside key stores',
        width: 48,
      }),
      ...styles,
    },
  ];

  return (
    <>
      <DashboardWidget
        label="Insights for Non-Human Identities"
        content={renderResourcesWithIconAndCount(
          orderBy(items, ['count'], ['desc']),
        )}
        styles={{ contentWrapper: { px: 0, pb: 0 } }}
        subHeader={formatNumberWithComma(total) + ' non-human identities'}
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        {...details}
      />
    </>
  );
};
